import { defineComponent as _defineComponent } from 'vue'
import { ObjectType, Offer, Offer as OfferGraphql, SraEventAction } from '@/@types/graphql-types';
import BuyBoxRow from '@/components/buybox/BuyBoxRow.vue';
import { EXCLUDED_PACKAGES } from '@/constants/buybox-excluded-packages.constant';
import { ImpressionTrackingEvents } from '@/enums/events';
import { useBackendTracking } from '@/helpers/composables/useBackendTracking';
import { useI18n } from '@/helpers/composables/useI18n';
import { OfferLocation, getBuyboxCategoryByMonetizationGraphql, trackClickOutGraphql } from '@/helpers/offer-helper';
import {
	SnowplowClickoutContextGraphql,
	SnowplowContext,
	SnowplowModuleContext,
	SnowplowTitleContextGraphql,
} from '@/helpers/tracking/providers';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { BuyboxOfferCategory } from '@/interfaces/buybox';
import type { TitleDetail } from '@/interfaces/title-details-graphql';
import { SponsoredAdFragment } from '@/pages/graphql/fragments/SponsoredAd.fragment';
import { NewBuyBoxView } from '@/stores/user.store';
import { computed } from 'vue';
import { TitleOfferFragment } from './graphql/fragments/Offer.fragment';

type BuyboxRowOffer = TitleOfferFragment & {
	iconUrl?: string;
	icons?: string[];
	bundleId?: number;
	bundleTechnicalName?: string;
};

type OfferCategories =
	| BuyboxOfferCategory.STREAM
	| BuyboxOfferCategory.RENT
	| BuyboxOfferCategory.BUY
	| BuyboxOfferCategory.BUNDLE;


export default /*#__PURE__*/_defineComponent({
  __name: 'PromotedBuyBox',
  props: {
    title: null,
    titleId: null,
    titleObjectType: null,
    titleObjectId: null,
    promotedOffers: { default: () => [] },
    sponsoredAd: { default: undefined },
    showDetails: { default: null },
    seasonNumber: { default: null },
    episodeNumber: { default: null },
    onlyBundle: { type: Boolean, default: false },
    isMiddleBuyBox: { type: Boolean, default: false },
    buyBoxSwitchType: null,
    additionalContexts: { default: () => [] }
  },
  setup(__props: any) {

const props = __props as { title: TitleDetail, titleId: string, titleObjectType: ObjectType, titleObjectId: number, promotedOffers: TitleOfferFragment[], sponsoredAd: SponsoredAdFragment | undefined, showDetails: {
			id: string;
			objectId: number;
			objectType: ObjectType;
			title: string;
		} | null, seasonNumber: number | null, episodeNumber: number | null, onlyBundle: boolean, isMiddleBuyBox: boolean, buyBoxSwitchType?: NewBuyBoxView, additionalContexts: (SnowplowModuleContext | SnowplowContext)[] };

const CategoryOrder = [
	BuyboxOfferCategory.STREAM,
	BuyboxOfferCategory.RENT,
	BuyboxOfferCategory.BUY,
	BuyboxOfferCategory.BUNDLE,
];



const { t } = useI18n();

const hasPromotedOffers = computed(() => {
	const filteredPromotedOffers = props.promotedOffers.filter(offer => {
		return !EXCLUDED_PACKAGES.value.includes(offer.package.packageId);
	});

	return filteredPromotedOffers.length > 0;
});

const categoryTranslatables = computed(() => {
	return {
		[BuyboxOfferCategory.STREAM]: t('WEBAPP_STREAM'),
		[BuyboxOfferCategory.RENT]: t('WEBAPP_RENT'),
		[BuyboxOfferCategory.BUY]: t('WEBAPP_BUY'),
		[BuyboxOfferCategory.BUNDLE]: t('WEBAPP_BUNDLES'),
	};
});

const categories = computed<OfferCategories[]>(() => {
	if (props.onlyBundle) {
		return [BuyboxOfferCategory.BUNDLE];
	}

	const updatedOffers = props.promotedOffers
		.map(offer => getBuyboxCategoryByMonetizationGraphql(offer.monetizationType))
		.filter(key => key !== undefined)
		.filter((item, index, offers) => offers.indexOf(item) === index) as OfferCategories[];

	updatedOffers.sort((a, b) => CategoryOrder.indexOf(a) - CategoryOrder.indexOf(b));

	return updatedOffers;
});

const sponsoredRecommendationCohort = computed(() => {
	return props.sponsoredAd?.holdoutGroup ? 0 : 1;
});

function getOffersByCategory(category: BuyboxOfferCategory): BuyboxRowOffer[] {
	// Start with the full list of offers
	let _promotedOffers = [...props.promotedOffers];

	// Filtering out TVOD providers we don't have a partnership with
	const filteredPromotedOffers = props.promotedOffers.filter(offer => {
		return !EXCLUDED_PACKAGES.value.includes(offer.package.packageId);
	});

	_promotedOffers = filteredPromotedOffers.filter(
		offer => getBuyboxCategoryByMonetizationGraphql(offer.monetizationType) === category
	);

	// Return filtered offers
	return _promotedOffers;
}

function clickOut(payload: { buyboxOffer: OfferGraphql; isBundle: boolean }) {
	const { buyboxOffer: offer } = payload;
	const contexts = [...props.additionalContexts];

	const sponsoredPackage = props.sponsoredAd?.campaign?.watchNowOffer.package;

	if (props.sponsoredAd?.bidId && payload.buyboxOffer.package.packageId === sponsoredPackage?.packageId) {
		const clickoutContext = SnowplowClickoutContextGraphql.fromProviderOffer(payload.buyboxOffer as Offer);
		const contexts = (props.additionalContexts || []).concat([
			new SnowplowTitleContextGraphql(props.titleObjectId, props.titleObjectType),
		]);
		contexts.push(clickoutContext);

		TrackingHelper.trackEvent(
			ImpressionTrackingEvents.SPONSORED_RECOMMENDATIONS,
			{
				action: 'buybox_clickout',
				label: `${props.titleId}_${sponsoredPackage.packageId}`,
				property: 'buybox',
				value: sponsoredRecommendationCohort.value,
			},
			contexts
		);

		// track influenced campaign metrics
		useBackendTracking().trackSraEvent({ action: SraEventAction.BuyboxClickout, sponsoredAd: props.sponsoredAd });
	}

	trackClickOutGraphql({
		offer,
		objectType: props.showDetails?.objectType ?? props.titleObjectType,
		objectId: props.showDetails?.objectId ?? props.titleObjectId,
		seasonNumber: props.seasonNumber,
		episodeNumber: props.episodeNumber,
		offerLocation: props.isMiddleBuyBox ? OfferLocation.MIDDLE_BUYBOX : OfferLocation.PROMOTED_BUYBOX,
		additionalContexts: contexts,
	});

	// showPageRolloutTracking

	// const clickoutContext = SnowplowClickoutContextGraphql.fromProviderOffer(payload.buyboxOffer);
	// TITLE_DETAIL_REDESIGN_NEW_BUYBOX
	// trackTitleDetailRedesignNewBuyBoxExp({
	// 	action: 'click',
	// 	label: 'clickout',
	// 	contexts: [clickoutContext, ...props.additionalContexts],
	// 	value: OfferLocation.PROMOTED_BUYBOX,
	// });
	// TITLE_DETAIL_REDESIGN_NEW_BUYBOX
}

return { __sfc: true,CategoryOrder, props, t, hasPromotedOffers, categoryTranslatables, categories, sponsoredRecommendationCohort, getOffersByCategory, clickOut, BuyBoxRow }
}

})